import React, { useContext } from 'react';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import { getArgumentsListColumnDefs } from 'routes/BudgetModule/components/BudgetOverview/AnalyseArguments/helpers/gridHelpers';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { Box, styled } from '@mui/material';
import LinkCellRenderer from 'routes/BudgetModule/components/BudgetProposal/BudgetVentilated/components/LinkCellRenderer';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { useTranslation } from 'react-i18next';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import { GetLocaleTextParams, SortChangedEvent } from 'ag-grid-community';
import {
    triggerOpenArgumentTracker,
    triggerSortArgumentTracker,
    useManageCloseArgumentsModalTracker,
} from '../helpers/trackersHelpers';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

type ArgumentsListGridProps = {
    rowData: ArgumentT[] | undefined;
    isOpen: boolean;
};

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    height: '75vh',
});

/**
 * A component that renders an AG Grid table for displaying a list of arguments.
 * The component uses the `getArgumentsListColumnDefs` function to generate column definitions
 *
 * @param rowData - The data to be displayed in the grid, it is a list of arguments.
 * @returns The rendered AG Grid table within a styled Box component.
 */

const ArgumentsListGrid = ({
    rowData,
    isOpen,
}: ArgumentsListGridProps): React.ReactElement => {
    const { t } = useTranslation();
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);
    const { incrementOpenedArgumentsCount } =
        useManageCloseArgumentsModalTracker(isOpen);

    const firstBudgetBy =
        rowData && rowData.length > 1
            ? rowData[0].budgetProposal.by
            : t('analyseArguments.resource');

    const onSortChanged = (event: SortChangedEvent) => {
        triggerSortArgumentTracker(event);
    };

    const onArgumentOpened = (data: ArgumentT) => {
        incrementOpenedArgumentsCount();
        triggerOpenArgumentTracker(data);
    };

    return (
        <StyGrid className="ag-theme-material">
            <AgGridReact
                rowData={rowData}
                columnDefs={getArgumentsListColumnDefs(
                    firstBudgetBy,
                    onArgumentOpened,
                    t,
                    isSchool,
                )}
                rowHeight={ROW_HEIGHT}
                headerHeight={HEADER_HEIGHT}
                components={{
                    textFloatingFilter: TextFloatingFilter,
                    linkCellRenderer: LinkCellRenderer,
                }}
                defaultColDef={{
                    flex: 1,
                    sortable: true,
                    resizable: false,
                    menuTabs: [],
                    suppressNavigable: true,
                }}
                statusBar={{
                    statusPanels: [
                        {
                            statusPanel: 'agTotalAndFilteredRowCountComponent',
                        },
                    ],
                }}
                getLocaleText={(params: GetLocaleTextParams) => {
                    if (params.key === 'totalAndFilteredRows')
                        return t('budget.arguments');
                    return params.defaultValue;
                }}
                onSortChanged={onSortChanged}
            />
        </StyGrid>
    );
};
export default ArgumentsListGrid;
